import { useEffect, useState } from "react";
import { Check, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Confirm, Pill } from "@/components/Common";
import { Button } from "@/components/ui/button";

const STATUS = {
  PENDING_PAYMENT: ["Menunggu pembayaran", "bg-amber-100 text-amber-900"],
  APPROVING: ["Sedang diproses", "bg-sky-100 text-sky-900"],
  APPROVED: ["Disetujui", "bg-green-100 text-green-900"],
  REJECTED: ["Ditolak", "bg-red-100 text-red-900"],
};

const rupiah = (amount) => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
}).format(amount || 0);

export function PaidRegistrations({ eventId }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/admin/events/${eventId}/registrations`)
    .then((response) => setItems(response.data.items))
    .catch((error) => toast.error(errMsg(error)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const decide = async (registration, action) => {
    try {
      const body = action === "reject"
        ? { reason: "Pembayaran belum dapat dikonfirmasi." }
        : {};
      await api.post(
        `/admin/events/${eventId}/registrations/${registration.id}/${action}`,
        body,
      );
      const messages = {
        approve: "Peserta disetujui.",
        reject: "Pengajuan ditolak.",
        "cancel-approval": "Persetujuan dibatalkan. Pengajuan kembali menunggu verifikasi.",
      };
      toast.success(messages[action]);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  if (!items.length) {
    return <p className="text-sm text-muted-foreground" data-testid="paid-registration-empty">Belum ada pengajuan pembayaran.</p>;
  }
  return (
    <div className="space-y-3" data-testid="paid-registrations-list">
      {items.map((registration) => {
        const [label, color] = STATUS[registration.status] || [registration.status, "bg-stone-100"];
        return (
          <div
            key={registration.id}
            className="flex flex-wrap items-center gap-3 border-b border-[#1A3323]/10 pb-3 last:border-0 last:pb-0"
            data-testid={`paid-registration-${registration.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[#1A3323]">{registration.profile_name}</div>
              <div className="text-xs text-muted-foreground">{rupiah(registration.price)}</div>
              {registration.rejection_reason && (
                <div className="mt-1 text-xs text-red-700">{registration.rejection_reason}</div>
              )}
            </div>
            <Pill className={color} testid={`paid-registration-status-${registration.id}`}>{label}</Pill>
            {registration.status === "PENDING_PAYMENT" && (
              <div className="flex gap-1.5">
                <Confirm
                  testid={`approve-payment-${registration.id}`}
                  title="Setujui pembayaran?"
                  desc="Peserta akan langsung bisa mengambil tiket tanpa check-in GPS."
                  confirmText="Setujui"
                  onConfirm={() => decide(registration, "approve")}
                  trigger={
                    <Button size="sm" data-testid={`approve-payment-button-${registration.id}`}>
                      <Check className="mr-1 h-4 w-4" /> Setujui
                    </Button>
                  }
                />
                <Confirm
                  testid={`reject-payment-${registration.id}`}
                  title="Tolak pengajuan?"
                  desc="Peserta dapat mengajukan ulang setelah pembayaran dikonfirmasi."
                  confirmText="Tolak"
                  destructive
                  onConfirm={() => decide(registration, "reject")}
                  trigger={
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-700"
                      data-testid={`reject-payment-button-${registration.id}`}
                    >
                      <X className="mr-1 h-4 w-4" /> Tolak
                    </Button>
                  }
                />
              </div>
            )}
            {registration.status === "APPROVED" && (
              <Confirm
                testid={`cancel-payment-approval-${registration.id}`}
                title="Batalkan persetujuan pembayaran?"
                desc="Kuota dikembalikan dan pengajuan peserta kembali menunggu verifikasi."
                destructive
                confirmText="Batalkan persetujuan"
                onConfirm={() => decide(registration, "cancel-approval")}
                trigger={
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-700"
                    data-testid={`cancel-payment-approval-button-${registration.id}`}
                  >
                    <RotateCcw className="mr-1 h-4 w-4" />
                    Batalkan Persetujuan
                  </Button>
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
}